/**
 * Buscar el manuscrito en el disco, sin teclear una ruta.
 *
 * POR QUÉ ESTÁ DENTRO DEL PANEL Y NO ES EL DIÁLOGO DEL SISTEMA. Probado en un Mac: el selector
 * nativo que abre el motor sale DETRÁS de la ventana del navegador —el motor corre como agente en
 * segundo plano y no puede traerse al frente sin permiso de Automatización—, así que el autor
 * pulsa y ve un panel que no reacciona. Un botón que parece no hacer nada es peor que no tenerlo.
 *
 * Esto siempre se ve, funciona igual en Mac y en Windows y no pide permisos. El recuento de
 * documentos por carpeta es lo que de verdad ahorra tiempo: se ve dónde está la novela sin entrar
 * carpeta por carpeta.
 */
import { useCallback, useEffect, useState } from "react";
import { carpetas } from "../api";
import type { Listado } from "../api";

export default function Explorador(
  { alElegir, alCerrar }: { alElegir: (ruta: string) => void; alCerrar?: () => void },
) {
  const [l, setL] = useState<Listado | null>(null);
  const [donde, setDonde] = useState<string | undefined>(undefined);
  const [fallo, setFallo] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);

  const ir = useCallback((ruta?: string) => {
    setCargando(true);
    setFallo(null);
    carpetas(ruta)
      .then((d) => { if (d.error) setFallo(d.error); else { setL(d); setDonde(d.ruta); } })
      .catch(() => setFallo("No he podido leer esa carpeta."))
      .finally(() => setCargando(false));
  }, []);

  useEffect(() => { ir(); }, [ir]);

  return (
    <section className="carta">
      <h2>Busca tu manuscrito
        <span className="sub">{l ? `${l.carpetas.length} carpetas · ${l.documentos.length} documentos` : "…"}</span>
      </h2>

      {/* Los sitios por donde se empieza a buscar. */}
      <div className="dir">
        {(l?.atajos ?? []).map((a) => (
          <button className="chip" key={a.ruta} onClick={() => ir(a.ruta)}>{a.nombre}</button>
        ))}
      </div>

      {/* Dónde estoy, y cómo volver atrás. */}
      <div className="dir">
        <button className="chip" onClick={() => l?.padre && ir(l.padre)} disabled={!l?.padre}>↑ subir</button>
        <span className="mono" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {donde ?? ""}
        </span>
      </div>

      {fallo && <p className="vacio" style={{ color: "var(--acc)" }}>{fallo}</p>}

      <div className="mapa">
        {cargando && <p className="vacio">Mirando…</p>}
        {!cargando && l?.carpetas.length === 0 && l?.documentos.length === 0 && (
          <p className="vacio">Esta carpeta está vacía.</p>
        )}
        {l?.carpetas.map((c) => (
          <button className="cap" key={c.ruta} onClick={() => ir(c.ruta)}
                  style={{ width: "100%", textAlign: "left", cursor: "pointer" }}>
            <span className="idx">›</span>
            <span className="tit" style={{ width: "auto", flex: 1 }}>{c.nombre}</span>
            <b className="mono" style={{ width: "auto" }}>
              {c.documentos ? `${c.documentos} doc.` : ""}
            </b>
          </button>
        ))}
      </div>

      {/* Los documentos de esta carpeta: la señal de que es la buena. */}
      {!!l?.documentos.length && (
        <p className="vacio">
          Aquí hay {l.documentos.length} {l.documentos.length === 1 ? "documento" : "documentos"}:{" "}
          {l.documentos.slice(0, 4).map((d) => d.nombre).join(", ")}
          {l.documentos.length > 4 ? "…" : ""}
        </p>
      )}

      <div className="dir">
        <button className="btn" onClick={() => donde && alElegir(donde)} disabled={!donde || cargando}>
          Usar esta carpeta
        </button>
        {alCerrar && <button className="btn fantasma" onClick={alCerrar}>Cancelar</button>}
      </div>
    </section>
  );
}
